import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { authForgotPassword, authResetPassword } from '../../config';

// Vague 4 — mot de passe oublié. Une seule page, deux modes :
// - sans ?token : demande (saisie email) -> webhook n8n envoie le lien
// - avec ?token : réinitialisation (nouveau mot de passe)
export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const isReset = Boolean(token);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (isReset) {
        await authResetPassword(token, password);
        setDone(true);
        setTimeout(() => navigate('/compte/connexion', { replace: true }), 2500);
      } else {
        await authForgotPassword(email);
        setDone(true);
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  const input = 'w-full bg-white border border-slate-200 rounded-sm px-3 py-2.5 text-sm text-slate-900 focus:border-secondary focus:ring-1 focus:ring-secondary/30 outline-none';
  const label = 'block font-accent text-[11px] uppercase tracking-widest text-slate-500 mb-1';

  return (
    <div className="min-h-screen bg-primary text-white flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-6">
            <img src="/logo-meteris.png" alt="Météris" className="h-16 w-16 rounded-full object-cover ring-2 ring-secondary/30" />
          </Link>
          <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
            {isReset ? 'Nouveau mot de passe' : 'Mot de passe oublié'}
          </h1>
        </div>

        <div className="bg-slate-50 text-slate-900 rounded-sm border border-slate-200 shadow-2xl p-6 md:p-8 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-sm p-3 text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {done ? (
            <div className="text-center space-y-3 py-2">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              {isReset ? (
                <p className="text-sm text-slate-600">Mot de passe modifié. Redirection vers la connexion…</p>
              ) : (
                <p className="text-sm text-slate-600">
                  Si un compte existe pour cet email, vous recevrez un lien de réinitialisation
                  (valable 1 heure). Pensez à vérifier vos spams.
                </p>
              )}
              <Link
                to="/compte/connexion"
                className="inline-block font-accent uppercase tracking-wider text-secondary hover:text-primary transition-colors text-xs"
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                {isReset
                  ? 'Choisissez un nouveau mot de passe (8 caractères minimum).'
                  : 'Saisissez votre email : nous vous enverrons un lien pour réinitialiser votre mot de passe.'}
              </p>

              {isReset ? (
                <div>
                  <label className={label}>Nouveau mot de passe</label>
                  <input
                    className={input}
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              ) : (
                <div>
                  <label className={label}>Email</label>
                  <input
                    className={input}
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-secondary text-white hover:bg-secondary/90 font-bold uppercase tracking-wider rounded-sm py-3 text-sm transition-all active:scale-95 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                {isReset ? 'Changer mon mot de passe' : 'Envoyer le lien'}
              </button>

              <p className="text-xs text-slate-500 text-center">
                <Link to="/compte/connexion" className="font-accent uppercase tracking-wider text-secondary hover:text-primary transition-colors">
                  Retour à la connexion
                </Link>
              </p>
            </form>
          )}
        </div>

        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 font-accent text-xs uppercase tracking-widest text-slate-400 hover:text-secondary transition-colors w-full justify-center"
        >
          <ArrowLeft className="w-3 h-3" />
          Retour au site
        </Link>
      </div>
    </div>
  );
}
